import { flow, getEnv } from "mobx-state-tree";

import { Column, ColumnType } from "../../components/datatable/interfaces";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import serializeToQueryParams from "../../helpers/QueryParams";
import { endpoints } from "../api/ApiEndpoints";
import { Project } from "./Project";
import ProjectInput from "./ProjectInput";
import { ProjectStatus } from "../../generated/Projects";

export const ProjectsStorageModel = DataGridStorageModel.named("ProjectsStorageModel")
  .actions(() => {
    return {
      getTranslatedStatus(status: ProjectStatus) {
        return `general.projects.status.${Object.keys(ProjectStatus).find(
          (key) => ProjectStatus[key] === status
        )}`;
      },
    };
  })
  .views((self) => ({
    get tableColumns(): Column[] {
      return [
        {
          key: "name",
          name: "general.name",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
          flex: 2,
        },
        {
          key: "format",
          name: "general.project.format",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "codeSystem",
          name: "general.project.codeSystem",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "status",
          name: "general.status",
          type: ColumnType.SingleSelect,
          valueOptions: [
            {
              label: self.getTranslatedStatus(ProjectStatus.Open),
              value: ProjectStatus.Open.toString(),
            },
            {
              label: self.getTranslatedStatus(ProjectStatus.Closed),
              value: ProjectStatus.Closed.toString(),
            },
          ],
          sortable: true,
          filterable: true,
        },
        {
          key: "deadline",
          name: "general.deadline",
          type: ColumnType.DateTime,
          sortable: true,
          filterable: true,
        },
        {
          key: "lead",
          name: "general.projects.lead",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "sewerObjectsCount",
          name: "general.sewer-objects",
          type: ColumnType.Number,
          sortable: true,
          filterable: true,
        },
        {
          key: "jobsCount",
          name: "general.jobs",
          type: ColumnType.Number,
          sortable: true,
          filterable: true,
        },
        {
          key: "membersCount",
          name: "general.projects.members",
          type: ColumnType.Number,
          sortable: true,
          filterable: true,
        },
        {
          key: "modified",
          name: "general.last-edit",
          type: ColumnType.DateTime,
          sortable: true,
          filterable: true,
        },
      ];
    },
  }))
  .actions((self) => {
    return {
      mapItem(item: any) {
        return new Project(item);
      },

      getFilteredProjects: flow(function* (searchText: string) {
        if (!searchText) return [];
        const endpoint = endpoints.get("projects_summary");
        endpoint.queryParams = serializeToQueryParams({ name: searchText });
        return yield getEnv(self).api.get(endpoint);
      }),

      createProject(project: ProjectInput) {
        const endpoint = endpoints.get("projects");
        return getEnv(self).api.post(endpoint, project);
      },

      deleteProjects: flow(function* (ids: number[]) {
        const endpoint = endpoints.get("projects");
        yield getEnv(self).api.delete(endpoint, ids);
        self.deselectAll();
        yield self.load();
      }),

      afterCreate() {
        self.defaultSort = { field: "name", sort: "asc" };
        self.gridColumns = self.tableColumns;
      },
    };
  })
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("projects_all");
      endpoint.queryParams = self.getDataQuery();
      return endpoint;
    },
  }));
